import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

/**
 * A password confirmation input component that validates password matching and minimum length.
 *
 * **Important:** Validation only occurs when the confirm password field changes, not when the
 * new password field changes. This means if a user enters matching passwords and then changes
 * the new password, the validation error won't appear until they modify the confirm password field.
 *
 * @param newPasswordName - The name attribute for the new password input field
 * @param confirmPasswordName - The name attribute for the confirm password input field
 * @param hasValue - Whether the password field already has a value (shows placeholder dots)
 */
function ConfirmPasswordInput({
    newPasswordName,
    confirmPasswordName,
    hasValue = false,
    disabled = false,
}: {
    newPasswordName?: string;
    confirmPasswordName?: string;
    hasValue?: boolean;
    disabled?: boolean;
}) {
    const [newPassword, setNewPassword] = React.useState<string>("");
    return (
        <>
            <Field className={"flex-1"}>
                <FieldLabel>{hasValue ? "Replace Password" : "New Password"}</FieldLabel>
                <Input
                    type={"password"}
                    name={newPasswordName}
                    placeholder={hasValue ? "••••••••" : ""}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
            </Field>
            <Field
                validate={(value) => {
                    const errors = [];
                    const confirmPassword = typeof value === "string" ? value : "";
                    if (confirmPassword !== newPassword) {
                        errors.push("Passwords must match");
                    }
                    // New users must always set a password; existing users only when they
                    // actually type a replacement (leaving both blank = keep current password).
                    const isSettingPassword = !hasValue || newPassword.length > 0 || confirmPassword.length > 0;
                    if (!disabled && isSettingPassword && confirmPassword.length < 8) {
                        errors.push("Password must be at least 8 characters");
                    }
                    return errors.length > 0 ? errors : null;
                }}
                className={"flex-1"}
            >
                <FieldLabel>{hasValue ? "Confirm Replace Password" : "Confirm New Password"}</FieldLabel>
                <Input type={"password"} name={confirmPasswordName} placeholder={hasValue ? "••••••••" : ""} />
                <FieldError className={"text-destructive"} />
            </Field>
        </>
    );
}

export type PasswordComponentProps = {
    newPasswordName?: string;
    confirmPasswordName?: string;
    enablePasswordName?: string;
    expirePasswordName?: string;
    hasValue?: boolean;
};

export function PasswordComponent({
    newPasswordName,
    confirmPasswordName,
    enablePasswordName,
    expirePasswordName,
    hasValue = false,
}: PasswordComponentProps) {
    const [isEnabled, setIsEnabled] = React.useState(hasValue);
    const [isExpired, setIsExpired] = React.useState(false);

    return (
        <>
            <div className={"flex flex-row gap-4"}>
                <Field className={"flex-row basis-0 grow justify-between"}>
                    <FieldLabel>Enable Password</FieldLabel>
                    <Switch name={enablePasswordName} checked={isEnabled} onCheckedChange={setIsEnabled} />
                </Field>
                <Field className={"flex-row-reverse basis-0 grow justify-between"}>
                    <Checkbox
                        className={"peer"}
                        name={expirePasswordName}
                        disabled={!isEnabled}
                        checked={isEnabled && isExpired}
                        onCheckedChange={setIsExpired}
                    />
                    <FieldLabel className={"peer-disabled:text-foreground/40"}>Expire Password on Login</FieldLabel>
                </Field>
            </div>
            {isEnabled && (
                <div className={"flex flex-row gap-4"}>
                    <ConfirmPasswordInput
                        disabled={!isEnabled}
                        newPasswordName={newPasswordName}
                        confirmPasswordName={confirmPasswordName}
                        hasValue={hasValue}
                    />
                </div>
            )}
        </>
    );
}
